import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Loader2, CheckCircle, AlertCircle, X, ArrowRight, ArrowLeft } from 'lucide-react';
import { uploadPDF } from '../services/api';

function UploadPage() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [showTitleDialog, setShowTitleDialog] = useState(false);
  const [showAIAssistanceDialog, setShowAIAssistanceDialog] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [aiAssistance, setAIAssistance] = useState('');
  const navigate = useNavigate();

  const handleFileSelect = (selectedFile) => {
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setCustomTitle(selectedFile.name.replace('.pdf', ''));
      setUploadStatus(null);
      // Show title dialog after file selection
      setShowTitleDialog(true);
    } else {
      setUploadStatus({ type: 'error', message: 'Please select a valid PDF file' });
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleTitleDialogNext = () => {
    if (customTitle.trim()) {
      setShowTitleDialog(false);
      setShowAIAssistanceDialog(true);
    }
  };

  const handleAIAssistanceDialogNext = () => {
    setShowAIAssistanceDialog(false);
    handleUpload();
  };

  const handleDialogCancel = () => {
    setShowTitleDialog(false);
    setShowAIAssistanceDialog(false);
    setFile(null);
    setCustomTitle('');
    setAIAssistance('');
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadStatus(null);

    try {
      const result = await uploadPDF(file, customTitle, aiAssistance);
      setUploadStatus({ type: 'success', message: 'PDF processed successfully!' });
      
      // Navigate to the article editor after a short delay
      setTimeout(() => {
        navigate(`/articles/${result.id}`);
      }, 2000);
      
    } catch (error) {
      setUploadStatus({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to process PDF. Please try again.' 
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border p-8">
        <div className="text-center mb-8">
          <FileText className="mx-auto h-16 w-16 text-primary-500 mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload PDF Article</h1>
          <p className="text-gray-600">
            Upload a PDF article to generate AI-powered summaries and video scripts
          </p>
        </div>

        {/* File Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            dragActive 
              ? 'border-primary-500 bg-primary-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".pdf"
            onChange={(e) => handleFileSelect(e.target.files[0])}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          
          <div className="space-y-2">
            <p className="text-lg font-medium text-gray-900">
              {file ? file.name : 'Drop your PDF here, or click to browse'}
            </p>
            <p className="text-sm text-gray-500">
              Only PDF files are supported
            </p>
          </div>
        </div>

        {/* Upload Status */}
        {uploadStatus && (
          <div className={`mt-6 p-4 rounded-lg flex items-center space-x-3 ${
            uploadStatus.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {uploadStatus.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span>{uploadStatus.message}</span>
          </div>
        )}

        {/* Upload Button - Only show if file is selected but no dialog is open */}
        {file && !showTitleDialog && !showAIAssistanceDialog && !uploading && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setShowTitleDialog(true)}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ArrowRight className="-ml-1 mr-3 h-5 w-5" />
              Continue to Upload
            </button>
          </div>
        )}

        {/* Processing Button - Show when uploading */}
        {uploading && (
          <div className="mt-8 text-center">
            <button
              disabled
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gray-400 cursor-not-allowed"
            >
              <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
              Processing PDF...
            </button>
          </div>
        )}

        {/* Processing Info */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-2">What happens next?</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• AI extracts and analyzes the article content</li>
            <li>• Generates a concise summary</li>
            <li>• Creates a 60-90 second short video script</li>
            <li>• Creates a 2-4 minute long-form video script</li>
            <li>• All content is saved and ready for editing</li>
          </ul>
        </div>
      </div>

      {/* Title Dialog */}
      {showTitleDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-medium text-gray-900">Set Article Title</h3>
              <button
                onClick={handleDialogCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Article Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter a descriptive title for this article"
                  autoFocus
                />
              </div>
              
              <div className="text-sm text-gray-600 mb-6">
                <p>Choose a clear, descriptive title that will help you identify this article later.</p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDialogCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTitleDialogNext}
                  disabled={!customTitle.trim()}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Assistance Dialog */}
      {showAIAssistanceDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-medium text-gray-900">AI Assistance</h3>
              <button
                onClick={handleDialogCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label htmlFor="ai-assistance" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Instructions (Optional)
                </label>
                <textarea
                  id="ai-assistance"
                  value={aiAssistance}
                  onChange={(e) => setAIAssistance(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Focus on the refinancing strategy and ROI calculations..."
                />
              </div>
              
              <div className="text-sm text-gray-600 mb-6">
                <p>Provide specific instructions to help the AI generate more targeted content. This will be added to the standard prompts.</p>
                <p className="mt-2 font-medium">Examples:</p>
                <ul className="mt-1 list-disc list-inside text-xs">
                  <li>"Focus on the refinancing strategy and ROI calculations"</li>
                  <li>"Emphasize the renovation timeline and cost breakdown"</li>
                  <li>"Highlight the rental yield and market analysis"</li>
                </ul>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAIAssistanceDialog(false);
                    setShowTitleDialog(true);
                  }}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </button>
                <button
                  onClick={handleAIAssistanceDialogNext}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Process PDF
                  <Upload className="ml-2 h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UploadPage;
